import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import { BrowserRouter, Link, Navigate, Route, Routes } from 'react-router';
import { AuthProvider, ErrorBoundary, Banner, Loading, ROLE_LABELS, TopBar, useAuth, useQuery, type MeResponse } from '@acc/web-shared';
import { AgencyLayout } from './agency';
import { BrandedLogin } from './BrandedLogin';
import { Dashboard } from './pages/Dashboard';
import { IntakeAssign, IntakeList, IntakeNew } from './pages/Intake';
import { QuoteList, QuoteNew } from './pages/Quotes';
import { QuoteDetail } from './pages/QuoteDetail';
import { Approvals, FollowUps, Sales } from './pages/Workflow';
import { CallForm, CallList } from './pages/Calls';
import { Team } from './pages/Team';
import { Offboard } from './pages/Offboard';
import { Reports } from './pages/Reports';
import { Resources } from './pages/Resources';
import { Audit } from './pages/Audit';
import { Archive } from './pages/Archive';
import { Search } from './pages/Search';
import { Settings } from './pages/Settings';

function Home({ me }: { me: MeResponse }) {
  const { user, signOut } = useAuth();
  if (me.memberships.length === 1) return <Navigate to={`/agencies/${me.memberships[0].agencyId}`} replace />;
  if (me.memberships.length === 0) {
    return (
      <div className="center">
        <div className="hero card">
          <h1>Your account isn't linked to an agency yet</h1>
          <p className="sub">You are signed in as <strong>{user?.username}</strong>. Ask your agency owner or ACC support to invite this email address.</p>
          <button type="button" className="btn" style={{ marginTop: 12 }} onClick={() => void signOut()}>Sign out</button>
        </div>
      </div>
    );
  }
  return (
    <main className="page">
      <h1>Choose an agency</h1>
      <p className="sub" style={{ marginBottom: 16 }}>You belong to more than one agency.</p>
      <div className="card">
        <table className="data">
          <tbody>
            {me.memberships.map((m) => (
              <tr key={m.agencyId}>
                <td><Link to={`/agencies/${m.agencyId}`}>{m.agencyName}</Link></td>
                <td className="muted">{ROLE_LABELS[m.role]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
}

function Gate() {
  const { ready, user } = useAuth();
  const me = useQuery<MeResponse>(ready && user ? '/v1/me' : null);
  if (!ready) return <Loading label="Signing in…" />;
  if (!user) return <BrandedLogin />;
  if (me.error) return <div className="page"><Banner tone="bad">Could not reach the ACC API: {me.error.message}</Banner></div>;
  if (!me.data) return <Loading label="Loading your agencies…" />;
  return (
    <Routes>
      <Route path="/" element={<Home me={me.data} />} />
      <Route path="/agencies/:agencyId" element={<AgencyLayout />}>
        <Route index element={<Dashboard />} />
        <Route path="intake" element={<IntakeList />} />
        <Route path="intake/new" element={<IntakeNew />} />
        <Route path="intake/:intakeId/assign" element={<IntakeAssign />} />
        <Route path="quotes" element={<QuoteList />} />
        <Route path="quotes/new" element={<QuoteNew />} />
        <Route path="quotes/:opportunityId" element={<QuoteDetail />} />
        <Route path="follow-ups" element={<FollowUps />} />
        <Route path="sales" element={<Sales />} />
        <Route path="approvals" element={<Approvals />} />
        <Route path="calls" element={<CallList />} />
        <Route path="calls/new" element={<CallForm />} />
        <Route path="calls/:callId" element={<CallForm key="edit" />} />
        <Route path="settings" element={<Settings />} />
        <Route path="team" element={<Team />} />
        <Route path="team/:membershipId/offboard" element={<Offboard />} />
        <Route path="reports" element={<Reports />} />
        <Route path="resources" element={<Resources />} />
        <Route path="audit" element={<Audit />} />
        <Route path="archive" element={<Archive />} />
        <Route path="search" element={<Search />} />
        <Route path="*" element={<Navigate to="." replace />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <BrowserRouter>
        <TopBar site="Agency" />
        <ErrorBoundary><Gate /></ErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
  </StrictMode>,
);
